import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["enableFullVirtualization"];
import { c as _c } from "react-compiler-runtime";
import { memo } from 'react';
import { useVirtualizer, observeElementRect as defaultObserveElementRect } from '@tanstack/react-virtual';
import Row from './Row';
import { TABLE_ROW_HEIGHT, TABLE_ROW_HEIGHT_BY_DENSITY, DEFAULT_COLUMN_WIDTH, SELECTION_COLUMN_WIDTH } from '../constants/sizes';
import { useScrollWindowRef } from '../context/ScrollWindowRefContext';
import { isNestedRow, useNestedRowObservers } from '../hooks/useNestedRowObservers';
import { VIRTUAL_INDEX_ATTRIBUTE } from '../constants/virtualization';
import { useWidthsSelector } from '../../redux/reduxHooks';
import { getWidths } from '../../resizing/redux/widthsSlice';
import { getColumnKey } from '../utils/getColumnKey';
import { useRowUpdateTimer } from '../../metrics/useRowUpdateTimer';
import { useTBodyMountTimer } from '../../metrics/useTBodyMountTimer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Consumers with typical pagination will have very few rows outside of the 25 row buffer.
// Consumers with infinite scroll can have many rows, so a medium sized buffer
// helps to smooth the virtualization experience without too much overhead.
const OVERSCAN_AMOUNT = 10;
const COLUMN_OVERSCAN_AMOUNT = 5;

// Custom measureElement that includes the height of any nested <tr>s
// (e.g. inline association tables) in the measured size of the parent row.
// This lets the virtualizer account for content it doesn't directly manage.
function measureElement(element, _entry, instance) {
  const dimension = instance.options.horizontal ? 'width' : 'height';
  const elementSize = element.getBoundingClientRect()[dimension];
  let size = elementSize;
  let sibling = element.nextElementSibling;
  while (sibling && isNestedRow(sibling)) {
    size += sibling.getBoundingClientRect()[dimension];
    sibling = sibling.nextElementSibling;
  }
  return size;
}
const NonVirtualizedTableBody = t0 => {
  const $ = _c(15);
  const {
    RowComponent,
    getRowId,
    rows,
    columns,
    selectable,
    numberOfFrozenColumns: t1,
    pageable,
    pageSize
  } = t0;
  const numberOfFrozenColumns = t1 === undefined ? 0 : t1;
  useRowUpdateTimer(rows, getRowId, pageable, pageSize);
  useTBodyMountTimer(pageable, pageSize);
  let t2;
  if ($[0] !== RowComponent || $[1] !== columns || $[2] !== getRowId || $[3] !== numberOfFrozenColumns || $[4] !== rows || $[5] !== selectable) {
    let t3;
    if ($[7] !== RowComponent || $[8] !== columns || $[9] !== getRowId || $[10] !== numberOfFrozenColumns || $[11] !== selectable) {
      t3 = (row, index) => {
        const rowId = getRowId(row);
        return /*#__PURE__*/_jsx(Row, {
          RowComponent: RowComponent,
          rowId: rowId,
          rowIndex: index,
          row: row,
          columns: columns,
          selectable: selectable,
          numberOfFrozenColumns: numberOfFrozenColumns
        }, rowId);
      };
      $[7] = RowComponent;
      $[8] = columns;
      $[9] = getRowId;
      $[10] = numberOfFrozenColumns;
      $[11] = selectable;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    t2 = rows.map(t3);
    $[0] = RowComponent;
    $[1] = columns;
    $[2] = getRowId;
    $[3] = numberOfFrozenColumns;
    $[4] = rows;
    $[5] = selectable;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[13] !== t2) {
    t3 = /*#__PURE__*/_jsx("tbody", {
      children: t2
    });
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
function buildVirtualColumnInfo(virtualColumns, nonFrozenColumnCount, estimateColumnSize) {
  if (virtualColumns.length === 0) {
    return {
      virtualColumns: [],
      leftSpacerWidth: 0,
      leftSpacerColspan: 0,
      rightSpacerWidth: 0,
      rightSpacerColspan: 0
    };
  }
  const firstVirtCol = virtualColumns[0];
  const lastVirtCol = virtualColumns[virtualColumns.length - 1];
  const leftSpacerColspan = firstVirtCol.index;
  let leftSpacerWidth = 0;
  for (let i = 0; i < leftSpacerColspan; i++) {
    leftSpacerWidth += estimateColumnSize(i);
  }
  const rightSpacerColspan = nonFrozenColumnCount - 1 - lastVirtCol.index;
  let rightSpacerWidth = 0;
  for (let i = lastVirtCol.index + 1; i < nonFrozenColumnCount; i++) {
    rightSpacerWidth += estimateColumnSize(i);
  }
  return {
    virtualColumns,
    leftSpacerWidth,
    leftSpacerColspan,
    rightSpacerWidth,
    rightSpacerColspan
  };
}
function useRowVirtualization(rows, dataDensity) {
  const {
    element: scrollElement
  } = useScrollWindowRef();
  const estimatedRowHeight = dataDensity ? TABLE_ROW_HEIGHT_BY_DENSITY[dataDensity].pixels : TABLE_ROW_HEIGHT;
  const getScrollElement = () => scrollElement;
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement,
    estimateSize: () => estimatedRowHeight,
    measureElement,
    overscan: OVERSCAN_AMOUNT,
    indexAttribute: VIRTUAL_INDEX_ATTRIBUTE,
    // When the scroll container has 0 height (e.g. table not yet in view),
    // the virtualizer won't render any rows. Reporting at least 1px ensures
    // rows are still rendered via overscan.
    observeElementRect: (instance, callback) => {
      return defaultObserveElementRect(instance, rect => {
        callback(Object.assign({}, rect, {
          height: Math.max(rect.height, 1)
        }));
      });
    }
  });
  const tbodyRef = useNestedRowObservers(virtualizer);
  return {
    virtualizer,
    tbodyRef,
    getScrollElement
  };
}
function useColumnVirtualization(columns, numberOfFrozenColumns, selectable, getScrollElement) {
  const widths = useWidthsSelector(getWidths);
  const getColumnWidth = col => {
    var _widths$key;
    if (!col) return DEFAULT_COLUMN_WIDTH;
    const key = getColumnKey(col.columnNamespace, col.columnName);
    return (_widths$key = widths[key]) !== null && _widths$key !== void 0 ? _widths$key : DEFAULT_COLUMN_WIDTH;
  };
  const nonFrozenColumnCount = columns.length - numberOfFrozenColumns;
  let frozenColumnsWidth = selectable ? SELECTION_COLUMN_WIDTH : 0;
  for (let i = 0; i < numberOfFrozenColumns; i++) {
    const col_0 = columns[i];
    if (col_0) {
      frozenColumnsWidth += getColumnWidth(col_0);
    }
  }
  const estimateColumnSize = index => getColumnWidth(columns[index + numberOfFrozenColumns]);
  const columnVirtualizer = useVirtualizer({
    count: nonFrozenColumnCount,
    getScrollElement,
    estimateSize: estimateColumnSize,
    horizontal: true,
    overscan: COLUMN_OVERSCAN_AMOUNT,
    paddingStart: frozenColumnsWidth
  });
  return buildVirtualColumnInfo(columnVirtualizer.getVirtualItems(), nonFrozenColumnCount, estimateColumnSize);
}
const VirtualizedTableBody = t0 => {
  const $ = _c(29);
  const {
    RowComponent,
    getRowId,
    rows,
    columns,
    selectable,
    numberOfFrozenColumns: t1,
    dataDensity,
    pageable,
    pageSize
  } = t0;
  const numberOfFrozenColumns = t1 === undefined ? 0 : t1;
  const {
    virtualizer,
    tbodyRef,
    getScrollElement
  } = useRowVirtualization(rows, dataDensity);
  const virtualColumnInfo = useColumnVirtualization(columns, numberOfFrozenColumns, selectable, getScrollElement);
  useRowUpdateTimer(rows, getRowId, pageable, pageSize);
  useTBodyMountTimer(pageable, pageSize);
  let bottomSpacerHeight;
  let t2;
  let t3;
  let t4;
  if ($[0] !== RowComponent || $[1] !== columns || $[2] !== getRowId || $[3] !== numberOfFrozenColumns || $[4] !== rows || $[5] !== selectable || $[6] !== tbodyRef || $[7] !== virtualColumnInfo || $[8] !== virtualizer) {
    var _firstVirtualRow$star, _lastVirtualRow$end;
    const virtualRows = virtualizer.getVirtualItems();
    const firstVirtualRow = virtualRows[0];
    const lastVirtualRow = virtualRows[virtualRows.length - 1];
    const topSpacerHeight = (_firstVirtualRow$star = firstVirtualRow === null || firstVirtualRow === void 0 ? void 0 : firstVirtualRow.start) !== null && _firstVirtualRow$star !== void 0 ? _firstVirtualRow$star : 0;
    bottomSpacerHeight = virtualizer.getTotalSize() - ((_lastVirtualRow$end = lastVirtualRow === null || lastVirtualRow === void 0 ? void 0 : lastVirtualRow.end) !== null && _lastVirtualRow$end !== void 0 ? _lastVirtualRow$end : 0);
    t2 = tbodyRef;
    t3 = topSpacerHeight > 0 && /*#__PURE__*/_jsx("tr", {
      "aria-hidden": "true",
      style: {
        height: topSpacerHeight,
        padding: 0,
        border: "none"
      }
    });
    let t5;
    if ($[13] !== RowComponent || $[14] !== columns || $[15] !== getRowId || $[16] !== numberOfFrozenColumns || $[17] !== rows || $[18] !== selectable || $[19] !== virtualColumnInfo || $[20] !== virtualizer.measureElement) {
      t5 = virtualRow => {
        const row = rows[virtualRow.index];
        const rowId = getRowId(row);
        return /*#__PURE__*/_jsx(Row, {
          RowComponent: RowComponent,
          rowId: rowId,
          rowIndex: virtualRow.index,
          row: row,
          columns: columns,
          selectable: selectable,
          numberOfFrozenColumns: numberOfFrozenColumns,
          measureElement: virtualizer.measureElement,
          virtualRowIndex: virtualRow.index,
          virtualColumnInfo: virtualColumnInfo
        }, rowId);
      };
      $[13] = RowComponent;
      $[14] = columns;
      $[15] = getRowId;
      $[16] = numberOfFrozenColumns;
      $[17] = rows;
      $[18] = selectable;
      $[19] = virtualColumnInfo;
      $[20] = virtualizer.measureElement;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    t4 = virtualRows.map(t5);
    $[0] = RowComponent;
    $[1] = columns;
    $[2] = getRowId;
    $[3] = numberOfFrozenColumns;
    $[4] = rows;
    $[5] = selectable;
    $[6] = tbodyRef;
    $[7] = virtualColumnInfo;
    $[8] = virtualizer;
    $[9] = bottomSpacerHeight;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    bottomSpacerHeight = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
  }
  let t5;
  if ($[22] !== bottomSpacerHeight) {
    t5 = bottomSpacerHeight > 0 && /*#__PURE__*/_jsx("tr", {
      "aria-hidden": "true",
      style: {
        height: bottomSpacerHeight,
        padding: 0,
        border: "none"
      }
    });
    $[22] = bottomSpacerHeight;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t2 || $[25] !== t3 || $[26] !== t4 || $[27] !== t5) {
    t6 = /*#__PURE__*/_jsxs("tbody", {
      ref: t2,
      children: [t3, t4, t5]
    });
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
};
const TableBody = t0 => {
  const $ = _c(7);
  let props;
  let t1;
  if ($[0] !== t0) {
    var _t = t0;
    ({
      enableFullVirtualization: t1
    } = _t);
    props = _objectWithoutPropertiesLoose(_t, _excluded);
    _t;
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const enableFullVirtualization = t1 === undefined ? false : t1;
  if (enableFullVirtualization) {
    let t2;
    if ($[3] !== props) {
      t2 = /*#__PURE__*/_jsx(VirtualizedTableBody, Object.assign({}, props));
      $[3] = props;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== props) {
    t2 = /*#__PURE__*/_jsx(NonVirtualizedTableBody, Object.assign({}, props));
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export default /*#__PURE__*/memo(TableBody);